import { useRef, useEffect } from 'react';

import useTextareaHeight from '@/hooks/V3/useTextareaHeight';
import { useRecoilState } from 'recoil';
import { aiChatBotSessionIdState } from '@/store/atom';
import { initialRequestState } from '@/store/atom';
import { useChatbotViewport } from '@/hooks/V3/useChatbotViewport';
import { ChatbotProvider } from './_context/ChatbotContext';
import AiChatbotInput from './_components/Bubble/AiChatbotInput';
import { useChatbotContext } from '@/pages/Lab/_context/ChatbotContext';
import { useGetAIChatbotAnswer } from '@/hooks/V3/useGetAIChatbotAnswer';
import { CHATBOT_INITIAL_MESSAGE } from './_constants/_const';
import AiChatbotMessages from './_components/Bubble/AiChatbotMessages';
import { useChatbotEffects } from '@/hooks/V3/useChatbotEffects';
import { isMobileState } from '@/store/atom';
import { SESSION_RESET_ALERT } from './_constants/_const';

export const AIChatBotPage = () => {
  return (
    <ChatbotProvider>
      <AIChatBot />
    </ChatbotProvider>
  );
};

const AIChatBot = () => {
  const [isMobile] = useRecoilState(isMobileState);
  const [sessionId, setSessionId] = useRecoilState(aiChatBotSessionIdState);
  const { messages, setIsThinking, isThinking, setMessages, cancelOngoingRequest } =
    useChatbotContext();
  const { getAiChatBotAnswer } = useGetAIChatbotAnswer();
  const [initialRequest] = useRecoilState(initialRequestState);

  /* 모바일 키보드 활성화에 따른 스크롤 영역 height 값 계산 */
  const scrollDivRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { textareaHeight, adjustHeight } = useTextareaHeight(inputRef, scrollDivRef);
  useChatbotViewport(scrollDivRef, textareaHeight);
  ////////////////////////////////

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useChatbotEffects({ messagesEndRef, inputRef, adjustHeight });

  useEffect(() => {
    if (!sessionId) {
      const lastMessageSessionId = messages[messages.length - 1]?.sessionId;
      if (lastMessageSessionId) {
        setSessionId(lastMessageSessionId);
      } else {
        if (!initialRequest) {
          setIsThinking(true);
          getAiChatBotAnswer({ messageText: '', skipMessageSave: true }).then(() => {
            setIsThinking(false);
          });
        }
      }
    }
  }, []);

  const handleResetSession = async () => {
    const lastMessage = messages[messages.length - 1];

    if (isThinking || lastMessage?.id === 0) return;
    cancelOngoingRequest();

    setMessages([...messages, SESSION_RESET_ALERT, CHATBOT_INITIAL_MESSAGE]);

    setIsThinking(true);
    getAiChatBotAnswer({ messageText: '', skipMessageSave: true }).then(() => {
      setIsThinking(false);
    });
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data);

      if (data['action'] === 'newChat') {
        handleResetSession();
      }
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage as EventListener);
    return () => {
      window.removeEventListener('message', handleMessage);
      document.addEventListener('message', handleMessage as EventListener);
    };
  }, [messages]);

  return (
    <div className={`flex w-full flex-col bg-white ${isMobile ? 'h-screen' : 'flex-1'}`}>
      {/* Chat Area */}
      <AiChatbotMessages scrollDivRef={scrollDivRef} messagesEndRef={messagesEndRef} />

      {/* Input Area */}
      <div className="sticky bottom-0  w-full bg-white">
        <AiChatbotInput inputRef={inputRef} />
      </div>
    </div>
  );
};
